/**
 * AgentProgressPage —— Figma Rebuild 重写版本
 *
 * 基于 Figma 节点 9281-13754 / 9519-137312 重新实现。
 * 布局结构：
 *   1. <AgentProgressHeader>          —— 标题 / 状态 / ProgressBar / Pause-Resume / Request Details
 *   2. 中间区域（左右并排）：
 *      - <InquiryDetailsCard>         —— 6 字段卡片
 *      - <ClarificationBoard>        —— 卡涩问题列表（无数据时展示兜底占位）
 *   3. Tab 栏：Reply (N) / Agent running log
 *   4. Tab 内容：
 *      - Reply: <SuppliersTable>      —— 供应商表格 + 分页
 *      - Agent running log: <AgentRunningLogSection>
 */

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import cn from 'classnames';
import { toast } from '@leadsnavi/ui-kit';
import { Layers, ScrollText } from 'lucide-react';

import Loader from '@/common/base/loader';
import { useInqiryProgress } from '@/features/inquiries/components/inquiry-card';
import useUserStore from '@/stores/userStore';
import { PagePath } from '@/routes/paths';
import ActiveTaskLimitModal from '@/components/active-task-limit-modal';

// 既有业务弹窗：spec 允许仅复用此一个 contact-progress 内的弹窗
import { InquiryRequestDetailDialogV2 } from '@/features/contact-progress/components/inquiry-request-detail-dialog';
import { SupplierDetailDrawer } from '@/features/chat-messages/components/supplier/supplier-detail/supplier-detail';

// 本 feature 内组件 / hooks / 适配层
import AgentProgressHeader from '../components/agent-progress-header';
import InquiryDetailsCard from '../components/inquiry-details-card';
import ClarificationBoard from '../components/clarification-board';
import SuppliersTable from '../components/suppliers-table';
import EditInquiryNameDialog from '../components/edit-inquiry-name-dialog';
import AgentRunningLogSection from '../components/agent-running-log-section';
import { replyTogglePut } from '@/services/swagger/easourcing/TaskReplyService';

import { useAgentInquiry } from '../hooks/useAgentInquiry';
import { useInquiryContacts } from '../hooks/useInquiryContacts';
import { useAgentProgressRefresh } from '../hooks/useAgentProgressRefresh';
import { buildAgentProgressViewModel } from '../lib/view-model';
import {
  AGENT_PROGRESS_PAGE_SIZE,
  type AgentProgressPageProps,
  type IApiInquiryDto,
  type SupplierRowVM,
} from '../types';

/** Tab 类型 */
type ProgressTab = 'reply' | 'log';

export const AgentProgressPage: FC<AgentProgressPageProps> = ({
  inquiryId,
  sessionId,
  onClose,
  onNameChange,
  onStateChange,
  className,
}) => {
  // ---------------------------------------------------------------------------
  // Data hooks
  // ---------------------------------------------------------------------------
  const inquiry = useAgentInquiry({ inquiryId, sessionId, onNameChange, onStateChange });
  const contacts = useInquiryContacts({ inquiryId, pageSize: AGENT_PROGRESS_PAGE_SIZE });
  const user = useUserStore(state => state.user);

  // 首次加载 detail
  useEffect(() => {
    void inquiry.refresh();
    // 仅在 inquiryId 变化时重拉
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inquiryId]);

  // ---------------------------------------------------------------------------
  // Tab state
  // ---------------------------------------------------------------------------
  const [activeTab, setActiveTab] = useState<ProgressTab>('reply');

  // ---------------------------------------------------------------------------
  // Clarification refresh key（由 push 消息递增触发 ClarificationBoard 重新拉取）
  // ---------------------------------------------------------------------------
  const [clarificationRefreshKey, setClarificationRefreshKey] = useState(0);
  const refreshClarifications = useCallback(() => {
    setClarificationRefreshKey(prev => prev + 1);
  }, []);

  // ---------------------------------------------------------------------------
  // US6 (T019) — WebSocket 刷新弹窗守卫
  // ---------------------------------------------------------------------------
  const anyDialogOpenRef = useRef(false);
  const pendingRefreshRef = useRef<{ inquiry: boolean; contacts: boolean }>({ inquiry: false, contacts: false });

  const guardedRefreshInquiry = useCallback(async () => {
    if (anyDialogOpenRef.current) {
      pendingRefreshRef.current.inquiry = true;
      return;
    }
    await inquiry.refresh();
  }, [inquiry]);

  const guardedRefreshContacts = useCallback(async () => {
    if (anyDialogOpenRef.current) {
      pendingRefreshRef.current.contacts = true;
      return;
    }
    await contacts.refresh();
  }, [contacts]);

  useAgentProgressRefresh({
    inquiryId,
    sessionId,
    refreshInquiry: guardedRefreshInquiry,
    refreshContacts: guardedRefreshContacts,
    refreshClarifications,
  });

  // ---------------------------------------------------------------------------
  // Derived data → VM
  // ---------------------------------------------------------------------------
  const stats = inquiry.stats;
  const contactProgress = useInqiryProgress(stats);

  const fallbackBuyerName = (() => {
    if (!user) return undefined;
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return fullName || user.email || undefined;
  })();

  const viewModel = useMemo(
    () =>
      buildAgentProgressViewModel({
        stats,
        inquiryInfo: inquiry.detail?.inquiryInfo,
        item: inquiry.detail?.item,
        contacts: contacts.contacts,
        page: contacts.page,
        pageSize: contacts.pageSize,
        total: contacts.total,
        progress: contactProgress,
        fallbackBuyerName,
      }),
    [
      stats,
      inquiry.detail?.inquiryInfo,
      inquiry.detail?.item,
      contacts.contacts,
      contacts.page,
      contacts.pageSize,
      contacts.total,
      contactProgress,
      fallbackBuyerName,
    ]
  );

  // ---------------------------------------------------------------------------
  // Local UI state（弹窗、selectedSupplier 等）
  // ---------------------------------------------------------------------------
  const navigate = useNavigate();
  const [showRequestDetailDialog, setShowRequestDetailDialog] = useState(false);
  const [isNameEditOpen, setIsNameEditOpen] = useState(false);

  // 公司详情侧边栏
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  // 任务限制弹窗
  const [showTaskLimitModal, setShowTaskLimitModal] = useState(false);
  const [runningInquiries, setRunningInquiries] = useState<IApiInquiryDto[]>([]);
  const [taskLimitLoading, setTaskLimitLoading] = useState(false);

  // US6 (T019) 弹窗守卫：聚合所有 dialog open 状态
  const anyDialogOpen = showRequestDetailDialog || isNameEditOpen || showTaskLimitModal;

  // 同步 ref，让 guardedRefresh*** 闭包始终拿到最新值
  useEffect(() => {
    anyDialogOpenRef.current = anyDialogOpen;
  }, [anyDialogOpen]);

  // 弹窗全部关闭瞬间 flush 任何挂起的 refresh
  useEffect(() => {
    if (anyDialogOpen) return;
    const pending = pendingRefreshRef.current;
    if (pending.inquiry || pending.contacts) {
      pendingRefreshRef.current = { inquiry: false, contacts: false };
      if (pending.inquiry) void inquiry.refresh();
      if (pending.contacts) void contacts.refresh();
    }
  }, [anyDialogOpen, inquiry, contacts]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  /** Pause / Resume 全部任务 */
  const handlePauseResume = useCallback(async () => {
    const result = await inquiry.toggleRunning();
    if (result.needConfirm) {
      setRunningInquiries(result.runningInquiries);
      setShowTaskLimitModal(true);
    }
    // toggleRunning 完成后也刷新 contacts
    void contacts.refresh();
  }, [inquiry, contacts]);

  /** 任务限制弹窗：替换一个运行中的任务，恢复当前任务 */
  const handleReplaceTask = useCallback(
    async (runningInquiryId: string) => {
      setTaskLimitLoading(true);
      try {
        await inquiry.replaceRunningInquiry(runningInquiryId);
        setShowTaskLimitModal(false);
        void contacts.refresh();
      } catch {
        // replaceRunningInquiry 内部已处理 toast
      } finally {
        setTaskLimitLoading(false);
      }
    },
    [inquiry, contacts]
  );

  const handleOpenRequestDetail = useCallback(async () => {
    setShowRequestDetailDialog(true);
    await inquiry.fetchRequestDetail();
  }, [inquiry]);

  const handleSaveName = useCallback(
    async (name: string) => {
      await inquiry.renameInquiry(name);
      setIsNameEditOpen(false);
    },
    [inquiry]
  );

  const handlePageChange = useCallback(
    (next: number) => {
      contacts.setPage(next);
    },
    [contacts]
  );

  const handleClickSupplierDetails = useCallback(
    (row: SupplierRowVM) => {
      navigate(generatePath(PagePath.supplierDetail, { contactId: String(row.contactId) }), {
        replace: true,
        state: { emailId: row.triggerMsgId },
      });
    },
    [navigate]
  );

  const handleCompanyClick = useCallback((row: SupplierRowVM) => {
    if (row.companyId) {
      setSelectedCompanyId(row.companyId);
    }
  }, []);

  const handleToggleAiReply = useCallback(
    async (id: string, next: boolean) => {
      // 通过 inquiryContactId 反查对应的 contactId
      const contact = contacts.contacts.find(c => c.inquiryContactId === id);
      if (!contact?.contactId) {
        toast.error('Unable to toggle AI Reply: contact not found');
        return;
      }
      try {
        await replyTogglePut({
          contactId: contact.contactId,
          enabled: next ? 1 : 0,
        });
        // 刷新列表以拿到最新开关状态
        await contacts.refresh();
      } catch (error) {
        console.error('Failed to toggle AI Reply:', error);
        toast.error('Failed to toggle AI Reply');
      }
    },
    [contacts]
  );

  // ---------------------------------------------------------------------------
  // US2 ClarificationBoard 回调
  // ---------------------------------------------------------------------------
  const handleClarificationReplied = useCallback(() => {
    void contacts.refresh();
    void inquiry.refresh();
  }, [contacts, inquiry]);

  // ---------------------------------------------------------------------------
  // Derived: Reply count for tab label
  // ---------------------------------------------------------------------------
  const replyCount = viewModel?.suppliersTable.total ?? 0;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  // VM 未就绪 → 展示 loader
  if (!viewModel) {
    return (
      <div className="h-full w-full">
        <div className="h-full w-full min-w-[600px] overflow-x-auto">
          <div
            className={cn(
              'h-full bg-white p-5 rounded-xl border border-[1.2px] border-[#E5E5E5] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.12)] overflow-y-auto justify-center items-center flex',
              className
            )}
          >
            <Loader />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <div className="h-full w-full min-w-[600px] overflow-x-auto">
        <div
          className={cn(
            'h-full bg-white p-5 rounded-xl border border-[1.2px] border-[#E5E5E5] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.12)] overflow-y-auto',
            className
          )}
        >
          {/* 1. Header */}
          <AgentProgressHeader
            vm={viewModel.header}
            onOpenNameEdit={() => setIsNameEditOpen(true)}
            onOpenRequestDetails={handleOpenRequestDetail}
            onPauseResume={handlePauseResume}
            operating={inquiry.operating}
            onClose={onClose}
          />

          {/* 2. 中间区域：Inquiry Details + Clarification Board 并排 */}
          <div className="flex gap-4 mb-5">
            {/* 左侧：Inquiry Details 固定宽度 */}
            <div className="flex-1">
              <InquiryDetailsCard vm={viewModel.inquiryDetails} />
            </div>

            {/* 右侧：Clarification Board（自管理列表 + 弹窗） */}
            <div className="flex-1 min-w-0">
              <ClarificationBoard
                inquiryTaskId={inquiryId}
                onReplied={handleClarificationReplied}
                refreshKey={clarificationRefreshKey}
              />
            </div>
          </div>

          {/* 3. Tab 栏：Reply / Agent running log */}
          <div className="flex items-center gap-3 border-b border-[#E5E5E5] mb-3">
            {/* Reply Tab */}
            <button
              type="button"
              onClick={() => setActiveTab('reply')}
              className={cn(
                'flex items-center gap-2 px-4 pb-3 pt-1 text-base font-medium transition-colors relative cursor-pointer',
                activeTab === 'reply' ? 'text-[#262626]' : 'text-[#737373] hover:text-[#262626]'
              )}
            >
              <Layers size={20} strokeWidth={1.75} />
              <span>Reply ({replyCount})</span>
              {activeTab === 'reply' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#171717] rounded-full" />
              )}
            </button>

            {/* Agent running log Tab */}
            <button
              type="button"
              onClick={() => setActiveTab('log')}
              className={cn(
                'flex items-center gap-2 px-4 pb-3 pt-1 text-base transition-colors relative cursor-pointer',
                activeTab === 'log' ? 'text-[#262626] font-medium' : 'text-[#737373] hover:text-[#262626]'
              )}
            >
              <ScrollText size={20} strokeWidth={1.75} />
              <span>Agent running log</span>
              {activeTab === 'log' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#171717] rounded-full" />
              )}
            </button>
          </div>

          {/* 4. Tab 内容 */}
          {activeTab === 'reply' ? (
            <SuppliersTable
              vm={viewModel.suppliersTable}
              loading={contacts.loading}
              onPageChange={handlePageChange}
              onToggleAiReply={handleToggleAiReply}
              onClickDetails={handleClickSupplierDetails}
              onCompanyClick={handleCompanyClick}
            />
          ) : (
            <div className="mt-4">
              <AgentRunningLogSection inquiryId={inquiryId} status={stats?.status} />
            </div>
          )}

          {/* 名称编辑弹窗 */}
          <EditInquiryNameDialog
            open={isNameEditOpen}
            defaultName={stats?.inquiryName ?? ''}
            loading={inquiry.operating}
            onOpenChange={setIsNameEditOpen}
            onSubmit={handleSaveName}
          />

          {/* Request Details 弹窗（业务复用） */}
          <InquiryRequestDetailDialogV2
            open={showRequestDetailDialog}
            onOpenChange={setShowRequestDetailDialog}
            data={inquiry.requestDetail}
            loading={inquiry.requestDetailLoading}
          />

          {/* 公司详情侧边栏 */}
          <SupplierDetailDrawer
            visible={!!selectedCompanyId}
            companyId={selectedCompanyId ?? undefined}
            supplier={null}
            isAiSummaryLoding={false}
            onClose={() => setSelectedCompanyId(null)}
          />

          {/* 任务限制弹窗 */}
          <ActiveTaskLimitModal
            open={showTaskLimitModal}
            onOpenChange={setShowTaskLimitModal}
            runningInquiries={runningInquiries}
            loading={taskLimitLoading}
            showSaveOption={false}
            onReplace={handleReplaceTask}
          />
        </div>
      </div>
    </div>
  );
};

export default AgentProgressPage;
